import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import api from "../../lib/api";
import { Share2, Users, DollarSign, Hash } from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dt = (s) => (s ? new Date(s).toLocaleString() : "—");

const AdminAffiliates = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/affiliates");
        setData(data);
      } catch (e) { /* noop */ }
      finally { setLoading(false); }
    })();
  }, []);

  const s = data?.summary || {};

  return (
    <AdminLayout title="Affiliates" subtitle="Admin Console">
      <div className="space-y-6" data-testid="admin-affiliates">
        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: DollarSign, label: "Total commissions paid", value: fmt(s.total_paid), accent: "text-emerald-400" },
            { icon: Hash, label: "Commission events", value: s.total_commissions ?? 0, accent: "text-sky-400" },
            { icon: Users, label: "Active affiliates", value: s.total_affiliates ?? 0, accent: "text-violet-400" },
          ].map((c, i) => (
            <div key={i} className="bb-card rounded-2xl p-5" data-testid={`affsum-${i}`}>
              <div className="flex items-center gap-2 text-white/50 text-[12px] mb-2">
                <c.icon size={15} className={c.accent} /> {c.label}
              </div>
              <div className="font-display font-bold text-[24px] text-white">{loading ? "…" : c.value}</div>
            </div>
          ))}
        </div>

        {/* Affiliates table */}
        <div className="bb-card rounded-2xl p-5" data-testid="affiliates-table">
          <div className="text-white text-[14px] font-medium mb-4 flex items-center gap-2">
            <Users size={16} className="text-[#ffae6a]" /> Affiliates
          </div>
          {(data?.affiliates || []).length === 0 ? (
            <div className="py-10 text-center text-white/40 text-[13px]">No affiliates with referrals or earnings yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-white/40 text-[11px] uppercase tracking-wider text-left">
                    <th className="py-2 font-medium">Affiliate</th>
                    <th className="py-2 font-medium">Code</th>
                    <th className="py-2 font-medium text-center">Directs</th>
                    <th className="py-2 font-medium text-center">Commissions</th>
                    <th className="py-2 font-medium text-right">Total earned</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {data.affiliates.map((a) => (
                    <tr key={a.id}>
                      <td className="py-2.5">
                        <div className="text-white/90">{a.name}</div>
                        <div className="text-[11px] text-white/40">{a.email}</div>
                      </td>
                      <td className="py-2.5 font-mono text-white/70 tracking-wider">{a.referral_code}</td>
                      <td className="py-2.5 text-center text-white/70">{a.directs}</td>
                      <td className="py-2.5 text-center text-white/70">{a.commission_count}</td>
                      <td className="py-2.5 text-right font-semibold text-emerald-300">{fmt(a.total_earned)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent commissions */}
        <div className="bb-card rounded-2xl p-5" data-testid="admin-commissions">
          <div className="text-white text-[14px] font-medium mb-4 flex items-center gap-2">
            <Share2 size={16} className="text-[#ffae6a]" /> Recent commission payouts
          </div>
          {(data?.recent_commissions || []).length === 0 ? (
            <div className="py-8 text-center text-white/40 text-[13px]">No commissions yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-white/40 text-[11px] uppercase tracking-wider text-left">
                    <th className="py-2 font-medium">Earner</th>
                    <th className="py-2 font-medium">From</th>
                    <th className="py-2 font-medium">Level</th>
                    <th className="py-2 font-medium">Deposit</th>
                    <th className="py-2 font-medium text-right">Amount</th>
                    <th className="py-2 font-medium text-right">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {data.recent_commissions.map((c) => (
                    <tr key={c.id}>
                      <td className="py-2.5 text-white/85">{c.earner_name}</td>
                      <td className="py-2.5 text-white/60">{c.from_user_name}</td>
                      <td className="py-2.5 text-white/60">L{c.level} ({Math.round(c.pct * 100)}%)</td>
                      <td className="py-2.5 text-white/60">{fmt(c.deposit_amount)}</td>
                      <td className="py-2.5 text-right font-semibold text-emerald-300">{fmt(c.amount)}</td>
                      <td className="py-2.5 text-right text-white/45">{dt(c.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAffiliates;
